import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Loader2, Lock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function AdminLogin() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem("yf_token", data.token);
      navigate("/admin");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-stone-950 text-stone-100 flex items-center justify-center px-4" data-testid="admin-login-page"
      style={{ background: "radial-gradient(circle at 75% 20%, rgba(217,119,6,0.12), transparent 45%), #0c0a09" }}>
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img src="/logo.png" alt="Yellowfin" className="w-20 h-20 rounded-full border border-stone-700 mx-auto mb-4" />
          <span className="text-amber-400 font-mono text-xs uppercase tracking-widest font-bold">Skipper Access</span>
          <h1 className="text-2xl font-extrabold uppercase tracking-tight text-white mt-2">Yellowfin Wheelhouse</h1>
        </div>
        <form onSubmit={submit} className="bg-stone-900 border border-stone-800 rounded-2xl p-8 space-y-5">
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-stone-300 mb-2">Email</label>
            <input data-testid="login-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="skipper email" autoComplete="username"
              className="w-full px-4 py-3 bg-stone-950 border border-stone-800 rounded-lg text-white text-sm focus:outline-none focus:border-amber-400 placeholder:text-stone-600" />
          </div>
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-stone-300 mb-2">Password</label>
            <input data-testid="login-password-input" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••" autoComplete="current-password"
              className="w-full px-4 py-3 bg-stone-950 border border-stone-800 rounded-lg text-white text-sm focus:outline-none focus:border-amber-400 placeholder:text-stone-600" />
          </div>
          {error && <div className="text-xs text-red-400 font-semibold" data-testid="login-error">{error}</div>}
          <button type="submit" data-testid="login-submit-btn" disabled={loading}
            className="w-full py-3.5 bg-amber-400 hover:bg-amber-300 disabled:opacity-60 text-stone-950 font-black text-sm uppercase tracking-wider rounded-lg transition-colors flex items-center justify-center gap-2">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}
            {loading ? "Signing In…" : "Sign In"}
          </button>
        </form>
        <div className="text-center mt-6">
          <Link to="/" className="text-xs text-stone-500 hover:text-amber-400" data-testid="login-back-link">← Back to site</Link>
        </div>
      </div>
    </div>
  );
}
